import { useMemo } from 'react';

// TODO: Re-enable after backend entitlement change
// import { useUserRoles } from '@features/user';
// import { ROLES } from '@utils/roleUtils';
import { useEntitlement } from '../store/api';
import type { EntitlementResponse } from '../types';
import {
  formatCapabilityName,
  getUsagePeriodLabel,
  isDepleted,
} from '../utils/entitlementFormatters';

export interface EntitlementStatus {
  /** Whether the user is blocked from using this capability (quota depleted OR non-clinician) */
  isBlocked: boolean;
  /** Whether the user is a non-clinician (admin without clinician role) */
  isNonClinician: boolean;
  /** Whether the entitlement data is loading */
  isLoading: boolean;
  /** Whether the entitlement data is currently being fetched (including refetches) */
  isFetching: boolean;
  /** The entitlement data if found */
  entitlement: EntitlementResponse | undefined;
  /** User-friendly tooltip message when blocked or non-clinician */
  tooltipMessage: string | null;
  /** Whether the feature gate is enabled */
  isFeatureEnabled: boolean;
}

/**
 * Hook to check entitlement status for a specific capability
 *
 * @param capabilityName - The capability name to check (e.g., 'note_creation', 'labs')
 * @returns EntitlementStatus object with blocking status, loading state, and tooltip message
 *
 * @example
 * ```tsx
 * const { isBlocked, tooltipMessage } = useEntitlementStatus('note_creation');
 *
 * <Button disabled={isBlocked || otherConditions}>
 *   Generate Note
 * </Button>
 * ```
 */
export function useEntitlementStatus(capabilityName: string): EntitlementStatus {
  // Get entitlement using dedicated entitlements endpoint
  // refetchOnMountOrArgChange ensures fresh data when component mounts (e.g., after note generation)
  // refetchOnFocus ensures fresh data when user returns to the browser tab
  const { entitlement, isLoading, isFetching, isFeatureEnabled } = useEntitlement(capabilityName, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });

  // Check user roles to determine if non-clinician
  // TODO: Re-enable after backend entitlement change
  // const roles = useUserRoles();
  // const isClinician = roles.includes(ROLES.CLINICIAN);
  const isClinician = true; // Temporarily default to true
  // Non-clinician blocking only applies when feature gate is enabled
  const isNonClinician = isFeatureEnabled && !isClinician;

  // Check if entitlement is depleted (quota exhausted)
  const quotaDepleted = entitlement ? isDepleted(entitlement) : false;

  // Blocked = quota depleted OR non-clinician (when feature enabled)
  const blocked = isNonClinician || quotaDepleted;

  // Generate tooltip message when blocked
  const tooltipMessage = useMemo<string | null>(() => {
    // Non-clinician message (matches EntitlementGate behavior)
    if (isNonClinician) {
      return 'As an admin, you do not have access to this feature. To unlock, please contact your practice administrator to upgrade your seat.';
    }

    // Quota depletion message
    if (!quotaDepleted || !entitlement) {
      return null;
    }

    const period = getUsagePeriodLabel(entitlement.usage_period ?? null);
    const periodText = period ? `${period} ` : '';
    const capabilityDisplayName = formatCapabilityName(entitlement.capability_name);
    const quota = entitlement.quota ?? 0;

    return `You've reached your ${periodText}limit of ${quota} ${capabilityDisplayName}. Please contact your practice administrator to upgrade.`;
  }, [isNonClinician, quotaDepleted, entitlement]);

  return {
    isBlocked: blocked,
    isNonClinician,
    isLoading,
    isFetching,
    entitlement,
    tooltipMessage,
    isFeatureEnabled,
  };
}
